import type { BoardData } from '../model/schema.js';
import { traceForward } from '../trace/trace.js';
import { inputList } from './input-list.js';
import { monitorList } from './monitor-list.js';
import { portLabel } from './helpers.js';

/**
 * Console interface (U48, OIL-22, R12): the board's channels and mixes as a documented,
 * versioned JSON document for console tools (wing-routing-table, scene generators). Everything
 * comes from the lists the board shows; nothing console-specific is guessed.
 */

export const CONSOLE_FORMAT = 'stageplottter.console.v1';

export interface ConsoleChannel {
  number: number;
  name: string;
  /** Source object or informal source text. */
  source: string;
  model: string;
  phantom: boolean;
  group: string;
  stand: string;
  insert: string;
  /** Stage-box input ("SB1 In 5" formally, else the informal text). */
  patch: string;
  /** Sub-core point (U44). */
  subPatch: string;
  /** The console input the signal arrives at, when the cabling reaches a console. */
  consoleInput: string;
  notes: string;
}

export interface ConsoleMix {
  number: number;
  name: string;
  kind: string;
  stereo: boolean;
  /** Channel numbers sent to the mix, with the level (dB) when set. */
  sends: { channel: number; level?: number }[];
  /** Monitors / IEMs it feeds. */
  outputs: string;
}

export interface ConsoleDocument {
  format: typeof CONSOLE_FORMAT;
  board: string;
  exportedAt: string;
  channels: ConsoleChannel[];
  mixes: ConsoleMix[];
}

/** The console input port a channel's signal reaches through the cabling, if any. */
function consoleInputOf(data: BoardData, channelId: string): string {
  const ch = data.channels[channelId];
  const src = ch?.sourceItemId;
  if (ch === undefined || src === undefined || data.items[src] === undefined) return '';
  const start =
    ch.sourcePortId === undefined ? { itemId: src } : { itemId: src, portId: ch.sourcePortId };
  for (const hop of traceForward(data, start)) {
    const to = hop.to;
    if (to.type === 'port' && data.items[to.itemId]?.kind === 'console')
      return portLabel(data, to.itemId, to.portId);
  }
  return '';
}

export function consoleDocument(data: BoardData, exportedAt: string): ConsoleDocument {
  const numberOf = new Map(Object.values(data.channels).map((c) => [c.id, c.number]));
  return {
    format: CONSOLE_FORMAT,
    board: data.meta.name,
    exportedAt,
    channels: inputList(data).map((r) => ({
      number: r.number,
      name: r.name,
      source: r.source,
      model: r.model,
      phantom: r.phantom,
      group: r.group,
      stand: r.stand,
      insert: r.insert,
      patch: r.patch,
      subPatch: r.subPatch,
      consoleInput: consoleInputOf(data, r.channelId),
      notes: r.notes,
    })),
    mixes: monitorList(data).map((m) => {
      const mix = data.mixes[m.mixId]!;
      return {
        number: mix.number,
        name: mix.name,
        kind: mix.kind,
        stereo: mix.stereo,
        sends: mix.sends
          .filter((s) => numberOf.has(s.channelId))
          .map((s) => ({
            channel: numberOf.get(s.channelId)!,
            ...(s.level !== undefined ? { level: s.level } : {}),
          }))
          .sort((a, b) => a.channel - b.channel),
        outputs: m.outputs,
      };
    }),
  };
}
